import { useState, type FormEvent } from "react";
import { subscribeToNewsletter } from "../../lib/newsletterSignup";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type Placement = "mobile" | "sidebar";

export default function NewsNewsletterCard({ placement }: { placement: Placement }) {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [error, setError] = useState("");
  const id = "grocery-newsletter-" + placement + "-email";
  const errorId = "grocery-newsletter-" + placement + "-error";

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (submitting) return;
    const normalized = email.trim();
    if (!EMAIL_PATTERN.test(normalized)) {
      setError("Please enter a valid email address.");
      return;
    }
    setError("");
    setSubmitting(true);
    try {
      const result = await subscribeToNewsletter({
        email: normalized,
        groups: [],
        source: "newsletter_page",
      });
      setSuccessMessage(
        result.alreadySubscribed
          ? "You are already on the list!"
          : "You're subscribed. Watch your inbox.",
      );
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Unable to subscribe right now.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      aria-label="Subscribe to the free grocery newsletter"
      className="rounded-2xl border border-[#DCE6DA] bg-white p-4 shadow-[0_6px_24px_rgba(23,62,47,0.045)] sm:p-5 lg:p-6"
    >
      <span aria-hidden="true" className="hidden" />
      <h2 className="mt-3 font-display text-[20px] font-extrabold leading-tight text-[#173E2F]">
        Get Grocery News by email
      </h2>
      <p className="mt-2 font-ui text-[13px] leading-[1.48] text-[#5C6D60]">
        The grocery news that matters. Recalls, savings and smarter shopping in your inbox.
      </p>
      {successMessage ? (
        <p role="status" aria-live="polite"
          className="mt-4 rounded-lg bg-[#E8F4E8] px-3 py-4 text-sm font-bold text-[#23583B]">
          {successMessage}
        </p>
      ) : (
        <form className="mt-4" onSubmit={handleSubmit}>
          <label htmlFor={id} className="mb-1.5 block font-ui text-[12px] font-semibold text-[#244C35]">
            Email address
          </label>
          <input
            id={id}
            type="email"
            name="email"
            required
            autoComplete="email"
            inputMode="email"
            placeholder="you@example.com"
            value={email}
            onChange={event => { setEmail(event.target.value); if (error) setError(""); }}
            aria-invalid={Boolean(error)}
            aria-describedby={error ? errorId : undefined}
            className="h-[45px] w-full rounded-lg border border-[#C5D5C6] bg-white px-3 font-ui text-[14px] text-[#173E2F] placeholder:text-[#89948C] focus:outline-none focus:ring-2 focus:ring-[#EFC06A]"
          />
          {error ? (
            <p id={errorId} role="alert" className="mt-2 text-[12px] text-red-700">{error}</p>
          ) : null}
          <button
            type="submit"
            disabled={submitting}
            aria-busy={submitting}
            className="mt-3 inline-flex min-h-[45px] w-full items-center justify-center rounded-lg bg-[#285D3E] px-4 font-ui text-[14px] font-bold text-white hover:bg-[#1F4B34] focus:outline-none focus:ring-2 focus:ring-[#EFC06A] focus:ring-offset-2 disabled:cursor-wait disabled:opacity-65"
          >
            {submitting ? "Subscribing..." : "Subscribe free"}
          </button>
        </form>
      )}
      <p className="mt-3 text-[11px] leading-relaxed text-[#718073]">
        For shoppers. No account required. Unsubscribe anytime.
      </p>
    </section>
  );
}
